import { Box, Container, Divider, Grid2, MenuItem, Pagination, Rating, Select, SelectChangeEvent, Stack, Typography } from "@mui/material"
import { graphql, useStaticQuery } from "gatsby"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { ReviewCard } from "../card"
import { ReviewContent } from "../reviewContent"
import { ReviewSummary } from "../summary"
import { ReviewsArray, ReviewsListProps } from "../types"
import { ReviewsListHeader } from "./header"
// import 'react-multi-carousel/lib/styles.css'



export const ReviewsList = ({ order, allowSort, featured, compact, list }: ReviewsListProps) => {
    // const elId = useId();
    // const elId2 = useId();
    // const elId3 = useId();
    // const elId4 = useId();
    const topReviewRef = useRef<HTMLDivElement>(null)

    const reviewsData = useStaticQuery<Queries.ReviewsListQuery>(graphql`
      query ReviewsList {
        reviewList: allJudgeMeReviewsList {
          nodes {
            id
            rating
            body
            featured
            published
            title
            verified
            created_at
            reviewer {
                name
            }
          }
        }
        judgeMeStoreReviewsRating {
          all_reviews_rating
        }
      }
    `);

    const allReviewsNotFiltered = useMemo(() => {
        return [
            ...reviewsData.reviewList.nodes.filter(e => e.published && e.rating >= 3)
        ];
    }, []);

    const getAllReviews = () => {
        const d = [
            ...allReviewsNotFiltered
                .filter(e => e.published && (featured ? e.featured : true))
                .filter(e => e.rating >= 3)
        ];

        const orderState = ordering;

        if (orderState === 'recent') {
            d.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        } else if (orderState === 'lowest') {
            d.sort((a, b) => a.rating - b.rating);
        } else if (orderState === 'highest') {
            d.sort((a, b) => b.rating - a.rating);
        } else {
            d.sort((a, b) => b.body.length - a.body.length);
        }
        return d;
    };

    const [ordering, setOrdering] = useState<ReviewsListProps['order']>(order || 'helpful');
    const [allReviews, setAllReviews] = useState<ReviewsArray>(getAllReviews());

    // Pagination state
    const [page, setPage] = useState(1);
    const itemsPerPage = 3;

    const pageCount = Math.ceil(allReviews.length / itemsPerPage);
    const paginatedReviews = allReviews.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const scrollTopReview = () => {
        if (topReviewRef.current) {
            topReviewRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        scrollTopReview()
        scrollTopReview()
    };

    const handleChange = (event: SelectChangeEvent) => {
        setOrdering(event.target.value as ReviewsListProps['order']);
        scrollTopReview()
    };

    useEffect(() => {
        setAllReviews(getAllReviews());
        setPage(1); // Reset to first page on sort/filter change        
    }, [ordering, featured]);

    const sorter = () => (
        <Select variant="standard" aria-label="Sort reviews by" value={ordering} onChange={handleChange}>
            <MenuItem value={'helpful'}>Most helpful</MenuItem>
            <MenuItem value={'recent'}>Recent</MenuItem>
            <MenuItem value={'highest'}>Highest rated</MenuItem>
            <MenuItem value={'lowest'}>Lowest rated</MenuItem>
        </Select>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 3 }} disableGutters>
            <Stack gap={4}>
                <Stack gap={1} justifyContent="center" alignItems="center">
                    {!list ? (
                        <ReviewsListHeader rating={reviewsData.judgeMeStoreReviewsRating.all_reviews_rating} count={allReviewsNotFiltered.length} list={list} compact={compact} />

                    ) : (
                        <Typography variant={compact || list ? 'h3' : 'h1'}>Reviews</Typography>
                    )}
                    {!list && (
                        <Rating
                            size="large"
                            readOnly
                            precision={0.1}
                            value={reviewsData.judgeMeStoreReviewsRating.all_reviews_rating}
                        />
                    )}
                    {allowSort && !list && sorter()}
                </Stack>

                {list ? (
                    <Stack gap={8}>
                        <Stack gap={2}>
                            <ReviewSummary
                                reviews={allReviewsNotFiltered as ReviewsArray}
                                totalAverage={reviewsData.judgeMeStoreReviewsRating.all_reviews_rating}
                            />

                            <Box
                                ref={topReviewRef} sx={{ pl: 3, pr: 3 }}>{allowSort && sorter()}</Box>
                        </Stack>

                        <Stack
                            gap={4}
                            direction="column"
                            sx={{ pl: 3, pr: 3, pb: 4 }}
                            divider={<Divider />}
                        >
                            {paginatedReviews.map(e => (
                                <Box key={e.id}>
                                    <ReviewContent {...e} />
                                </Box>
                            ))}
                        </Stack>

                        {/* Pagination controls */}
                        {pageCount > 1 && (
                            <Stack alignItems="center" sx={{ pb: 4 }}>
                                <Pagination count={pageCount} page={page} onChange={handlePageChange} />
                            </Stack>
                        )}
                    </Stack>
                ) : (
                    <Grid2 container spacing={2} direction="row" alignItems="stretch">
                        {allReviews.map(e => (
                            <Grid2 key={e.id} size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
                                <ReviewCard {...e} />
                            </Grid2>
                        ))}
                    </Grid2>
                )}
            </Stack>
        </Container>
    );
};
